import React, { useCallback, useMemo } from "react"

import { useRunTransportInvalidation, useUpdateRunTagsTransport } from "@/runs"
import { toUpdateRunTags } from "@/runs/values"
import { BulkTagEditor } from "@/tags"

import type { RunsTableResult } from "@/types/tables/RunsTable"

const RUN_NOUN = { singular: "simulation", plural: "simulations" }

interface RunsBulkTagDropdownProps {
  currentData: RunsTableResult[]
  isDisabled?: boolean
  selectedKeys: Set<string>
}

/**
 * Tag assignment for a multi-row run selection: the shared bulk editor wired to
 * the run tag write and the surfaces that read it.
 */
export const RunsBulkTagDropdown: React.FC<RunsBulkTagDropdownProps> = ({
  currentData,
  isDisabled = false,
  selectedKeys,
}) => {
  const { invalidateRunDetail, invalidateRunsLists } =
    useRunTransportInvalidation()
  const updateTagsMutation = useUpdateRunTagsTransport()

  // The selection outlives the rows: a write can move a run out of the active
  // filter, and the list drops its row while the run stays selected.
  const runIds = useMemo(() => Array.from(selectedKeys), [selectedKeys])
  const loadedRuns = useMemo(
    () => currentData.filter((run) => selectedKeys.has(run.id)),
    [currentData, selectedKeys],
  )

  const updateRunTags = useCallback(
    (runId: string, tags: string[]) =>
      updateTagsMutation.mutateAsync({
        run_id: runId,
        tags: toUpdateRunTags(tags),
      }),
    [updateTagsMutation],
  )

  const invalidateTaggedRuns = useCallback(
    async (taggedRunIds: string[]) => {
      await Promise.all([
        invalidateRunsLists(),
        ...taggedRunIds.map((runId) => invalidateRunDetail(runId)),
      ])
    },
    [invalidateRunDetail, invalidateRunsLists],
  )

  return (
    <BulkTagEditor
      invalidateTagged={invalidateTaggedRuns}
      isDisabled={isDisabled}
      loadedRecords={loadedRuns}
      noun={RUN_NOUN}
      recordIds={runIds}
      resource="runs"
      updateTags={updateRunTags}
    />
  )
}
